import React, { useState, useEffect, useRef } from "react";
import { Quote, CircleCheck, ArrowLeft, ArrowRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const testimonials = [{
  author: "Katarina M",
  quote: "Vandra Enkelt lever verkligen upp till sitt namn. Att ha all info samlat i en interaktiv guide istället för att googla i timmar är guld värt.",
  position: "Vandrare & Livsnjutare",
  project: "Vandring Amalfikusten"
}, {
  author: "Marcus Berg",
  quote: "Guiden till Amalfikusten är fantastiskt strukturerad. Beskrivningen av bagagetransporten är ett hack som kommer spara resenärer tusentals kronor.",
  position: "",
  project: ""
}, {
  author: "Sofia A",
  quote: "Äntligen ett sätt att tryggt vandra på egen hand utan att betala överpriser till resebyråer. Proffsigt, detaljerat och enkelt! Grym digital karta blev bästa vännen.",
  position: "Självständig Resenär",
  project: "Mallorca GR221"
}, {
  author: "Johan H",
  quote: "Hotelltipsen var guld värda. Vi bodde på fantastiska ställen som vi aldrig hade hittat på egen hand. Rekommenderas varmt.",
  position: "Naturälskare",
  project: "Vandring i Toscana"
}, {
  author: "Elena S",
  quote: "Kartorna med dag för dag beskrivningar är väldigt användbara. Allt man behöver för ett lyckat äventyr ryms i en smidig mobil-guide.",
  position: "Rastlös Motionär",
  project: "Höga Kustenleden"
}];
export default function SagtOmVaraByggprojekt() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const sectionRef = useRef<HTMLElement | null>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const [cursorVariant, setCursorVariant] = useState<"prev" | "next">("next");
  const {
    ref: revealRef,
    isVisible
  } = useRevealOnScroll();
  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (!sectionRef.current || !cursorRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    cursorRef.current.style.transform = `translate(${x}px, ${y}px)`;
    if (x < rect.width / 2) {
      setCursorVariant("prev");
    } else {
      setCursorVariant("next");
    }
  };
  const handleSectionClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, a')) return;
    if (cursorVariant === "prev") {
      api?.scrollPrev();
    } else {
      api?.scrollNext();
    }
  };
  return <section data-section-id="67783" id="vad-våra-vandrare-säger" ref={node => {
    sectionRef.current = node;
    revealRef.current = node;
  }} className={cn("relative w-full py-24 overflow-hidden dark bg-background text-foreground transition-opacity duration-700 group", isVisible ? "opacity-100" : "opacity-0", "md:cursor-none")} onMouseMove={handleMouseMove} onClick={handleSectionClick}>
      {}
      <div ref={cursorRef} className="pointer-events-none absolute top-0 left-0 z-50 hidden md:flex items-center justify-center size-16 rounded-full bg-primary text-primary-foreground shadow-xl transition-opacity duration-200 ease-out -mt-8 -ml-8 opacity-0 group-hover:opacity-100" aria-hidden="true">
        {cursorVariant === "prev" ? <ArrowLeft className="size-6" /> : <ArrowRight className="size-6" />}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <Badge variant="outline" className="mb-4 border-primary/20 text-primary bg-primary/5 uppercase tracking-widest font-mono text-[10px]">
            Omdömen
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 text-foreground">Vad våra testvandrare säger</h2>
          <p className="text-muted-foreground text-lg leading-relaxed">Innan vi släpper en guide låter vi erfarna vandrare testa formatet, kartorna och logistiken. Här är deras omdömen om våra digitala verktyg.</p>
        </div>

        <Carousel setApi={setApi} opts={{
        align: "center",
        loop: true
      }} className="w-full max-w-6xl mx-auto">
          <CarouselContent className="-ml-4 items-center">
            {testimonials.map((item, index) => {
            const isActive = index === current;
            return <CarouselItem key={index} data-index={index} className="pl-4 md:basis-1/2 lg:basis-1/3 transition-all duration-500 ease-out">
                  <div className={cn("transition-all duration-500 ease-out transform", isActive ? "scale-100 opacity-100 blur-0 z-10" : "scale-90 opacity-40 blur-[2px] z-0")}>
                    <Card className="border-none shadow-none bg-transparent">
                      <CardContent className="p-6 flex flex-col items-center text-center">
                        <Quote className="size-10 text-primary/30 mb-6 fill-primary/10" />
                        
                        <blockquote className="font-serif text-xl md:text-2xl leading-relaxed mb-8 text-foreground italic">
                          "{item.quote}"
                        </blockquote>

                        <div className="flex flex-col items-center gap-2">
                          <div className="font-bold text-lg tracking-tight text-foreground">{item.author}</div>
                          <div className="text-sm text-muted-foreground font-medium">{item.position}</div>
                          
                          
                          
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>;
          })}
          </CarouselContent>
        </Carousel>

        {}
        <div className="flex md:hidden justify-center gap-2 mt-8">
          {testimonials.map((_, i) => <Button key={i} variant="ghost" size="sm" className={cn("p-0 h-1 rounded-full transition-all duration-300 hover:bg-primary/50", i === current ? "bg-primary w-8" : "bg-primary/20 w-4")} onClick={e => {
          e.stopPropagation();
          api?.scrollTo(i);
        }} aria-label={`Gå till omdöme ${i + 1}`} />)}
        </div>
      </div>
    </section>;
}